// AudioWorkletProcessor hosting the MetronomeCore. Runs on the audio rendering
// thread: immune to main-thread jank and background-tab timer throttling. All
// control arrives as messages between render quanta (so every quantum sees one
// coherent parameter set); events go back the same way with frame stamps.

import { MetronomeCore } from '../core/MetronomeCore';
import type { HostMessage, WorkletMessage } from '../core/protocol';
import { MAX_BEATS, MAX_POLY_RINGS, MAX_SAMPLE_SLOTS, MAX_SEQ_TRACKS } from '../core/constants';

// AudioWorkletGlobalScope globals (not in the DOM lib typings).
declare const sampleRate: number;
declare const currentFrame: number;
declare function registerProcessor(name: string, ctor: unknown): void;
declare class AudioWorkletProcessor {
  readonly port: MessagePort;
  constructor(options?: unknown);
}

import { PROCESSOR_NAME } from './processor-name';

class BeatmateProcessor extends AudioWorkletProcessor {
  private readonly core = new MetronomeCore(sampleRate);

  constructor() {
    super();
    this.port.onmessage = (e: MessageEvent<HostMessage>) => this.handle(e.data);
    this.post({ type: 'ready', sampleRate });
  }

  private post(msg: WorkletMessage): void {
    this.port.postMessage(msg);
  }

  private handle(msg: HostMessage): void {
    const c = this.core;
    switch (msg.type) {
      case 'start':
        c.start(msg.withCountIn);
        break;
      case 'stop':
        c.stop();
        break;
      case 'mode':
        c.mode = msg.mode;
        break;
      case 'params': {
        const p = msg.params;
        if (p.unitsPerMinute !== undefined) c.unitsPerMinute = p.unitsPerMinute;
        if (p.beatsPerMeasure !== undefined) c.beatsPerMeasure = p.beatsPerMeasure;
        if (p.beatUnits) for (let i = 0; i < MAX_BEATS; i++) c.beatUnits[i] = p.beatUnits[i] ?? 1;
        if (p.beatTicks) for (let i = 0; i < MAX_BEATS; i++) c.beatTicks[i] = p.beatTicks[i] ?? 1;
        if (p.swing !== undefined) c.swing = p.swing;
        if (p.countInBars !== undefined) c.countInBars = p.countInBars;
        if (p.gapPlayBars !== undefined) c.gapPlayBars = p.gapPlayBars;
        if (p.gapMuteBars !== undefined) c.gapMuteBars = p.gapMuteBars;
        if (p.stopAfterBars !== undefined) c.stopAfterBars = p.stopAfterBars;
        if (p.stepPattern) c.stepPattern.set(p.stepPattern.subarray(0, c.stepPattern.length));
        if (p.levelGain) for (let i = 0; i < 3; i++) c.levelGain[i] = p.levelGain[i] ?? 1;
        if (p.volume) { c.volLeft = p.volume[0]; c.volRight = p.volume[1]; }
        if (msg.reschedule) c.rescheduleRequested = true;
        break;
      }
      case 'poly': {
        const p = msg.params;
        if (p.ringCount !== undefined) c.polyRingCount = p.ringCount;
        if (p.cycleBeats !== undefined) c.polyCycleBeats = p.cycleBeats;
        if (p.ringSteps) for (let r = 0; r < MAX_POLY_RINGS; r++) c.polyRingSteps[r] = p.ringSteps[r] ?? 4;
        if (p.ringLevels) c.polyRingLevels.set(p.ringLevels.subarray(0, c.polyRingLevels.length));
        if (p.ringGain) for (let r = 0; r < MAX_POLY_RINGS; r++) c.polyRingGain[r] = p.ringGain[r] ?? 1;
        break;
      }
      case 'seq': {
        const p = msg.params;
        if (p.trackCount !== undefined) c.seqTrackCount = p.trackCount;
        if (p.trackSteps) for (let t = 0; t < MAX_SEQ_TRACKS; t++) c.seqTrackSteps[t] = p.trackSteps[t] ?? 16;
        if (p.trackLevels) c.seqTrackLevels.set(p.trackLevels.subarray(0, c.seqTrackLevels.length));
        if (p.trackSample) for (let t = 0; t < MAX_SEQ_TRACKS; t++) c.seqTrackSample[t] = p.trackSample[t] ?? -1;
        if (p.trackGain) for (let t = 0; t < MAX_SEQ_TRACKS; t++) c.seqTrackGain[t] = p.trackGain[t] ?? 1;
        if (p.swing !== undefined) c.swing = p.swing;
        if (p.punch !== undefined) c.seqPunch = p.punch;
        if (p.room !== undefined) c.seqRoom = p.room;
        break;
      }
      case 'tones':
        c.tones = { accent: msg.accent, secondary: msg.secondary, normal: msg.normal, sub: msg.sub, poly: c.tones.poly };
        break;
      case 'ringTones':
        if (msg.ring >= 0 && msg.ring < MAX_POLY_RINGS) {
          c.tones.poly[msg.ring * 2] = msg.normal;
          c.tones.poly[msg.ring * 2 + 1] = msg.accent;
        }
        break;
      case 'sample':
        if (msg.slot >= 0 && msg.slot < MAX_SAMPLE_SLOTS) c.samples[msg.slot] = msg.data;
        break;
      case 'preview':
        c.preview(msg.kind, msg.index);
        break;
    }
  }

  process(_inputs: Float32Array[][], outputs: Float32Array[][]): boolean {
    const out = outputs[0];
    if (!out || out.length === 0) return true;
    const L = out[0];
    const R = out[1] ?? L;
    // The worklet's frame counter is the engine clock: event frames map to
    // AudioContext time as frame / sampleRate.
    this.core.framePos = currentFrame;
    this.core.process(L, R, L.length);
    const events = this.core.events;
    if (events.length > 0) {
      this.post({ type: 'events', events, frame: currentFrame });
      this.core.events = [];
    }
    return true;
  }
}

registerProcessor(PROCESSOR_NAME, BeatmateProcessor);
